import React, { useState, useEffect } from 'react';
import { API_BASE_URL, fetchWithAuth } from '../config';

export default function ComplianceLey21719({ usuario }) {
    const [activeTab, setActiveTab] = useState('consentimientos');
    const [consentimientos, setConsentimientos] = useState([]);
    const [auditLog, setAuditLog] = useState([]);
    const [breaches, setBreaches] = useState([]);
    const [retentionPolicies, setRetentionPolicies] = useState([]);
    const [dpaStatus, setDpaStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchRut, setSearchRut] = useState('');
    const [searchResult, setSearchResult] = useState(null);
    const [searchLoading, setSearchLoading] = useState(false);

    // Modal states
    const [showBreachModal, setShowBreachModal] = useState(false);
    const [breachForm, setBreachForm] = useState({
        titulo: '', descripcion: '', severidad: 'media',
        afectados_estimados: 0, tipos_dato_comprometidos: ''
    });

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        setLoading(true);
        try {
            const [auditRes, breachesRes, retentionRes, dpaRes, consRes] = await Promise.all([
                fetchWithAuth(`${API_BASE_URL}/api/audit-log?limit=100`),
                fetchWithAuth(`${API_BASE_URL}/api/data-breaches`),
                fetchWithAuth(`${API_BASE_URL}/api/retention-policies`),
                fetchWithAuth(`${API_BASE_URL}/api/compliance/aws-dpa`),
                fetchWithAuth(`${API_BASE_URL}/api/consentimientos`)
            ]);

            if (auditRes.ok) setAuditLog(await auditRes.json());
            if (breachesRes.ok) setBreaches(await breachesRes.json());
            if (retentionRes.ok) setRetentionPolicies(await retentionRes.json());
            if (dpaRes.ok) setDpaStatus(await dpaRes.json());
            if (consRes.ok) setConsentimientos(await consRes.json());
        } catch (err) {
            setError('Error al cargar datos de cumplimiento');
        } finally {
            setLoading(false);
        }
    };

    const handleSearchPersonal = async () => {
        if (!searchRut.trim()) return;
        setSearchLoading(true);
        setSearchResult(null);
        try {
            const res = await fetchWithAuth(`${API_BASE_URL}/api/personal/buscar?dni=${encodeURIComponent(searchRut)}`);
            if (res.ok) {
                const data = await res.json();
                if (data && data.id) {
                    const [consRes] = await Promise.all([
                        fetchWithAuth(`${API_BASE_URL}/api/consentimientos/${data.id}`)
                    ]);
                    if (consRes.ok) {
                        const cons = await consRes.json();
                        setSearchResult({ persona: data, consentimientos: cons });
                    } else {
                        setSearchResult({ persona: data, consentimientos: [] });
                    }
                } else {
                    setError('Persona no encontrada');
                }
            } else {
                setError('Persona no encontrada');
            }
        } catch (err) {
            setError('Error de conexión');
        } finally {
            setSearchLoading(false);
        }
    };

    const handleRevokeConsent = async (idPersonal, tipoDato) => {
        if (!window.confirm(`¿Confirmas la revocación del consentimiento para "${tipoDato}"? Esta acción impedirá el procesamiento de datos biométricos para esta persona.`)) return;
        try {
            const res = await fetchWithAuth(`${API_BASE_URL}/api/consentimientos/${idPersonal}/revocar`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tipo_dato: tipoDato })
            });
            if (res.ok) {
                alert('Consentimiento revocado exitosamente');
                if (searchResult) {
                    handleSearchPersonal();
                }
            } else {
                const data = await res.json();
                alert(data.error || 'Error al revocar');
            }
        } catch (err) {
            alert('Error de conexión');
        }
    };

    const handleARCO = async (idPersonal, derecho) => {
        const endpoints = {
            acceso: { method: 'GET', path: `/api/datos-personales/${idPersonal}/acceso` },
            portabilidad: { method: 'GET', path: `/api/datos-personales/${idPersonal}/portabilidad` },
            supresion: { method: 'DELETE', path: `/api/datos-personales/${idPersonal}/supresion` },
            oposicion: {
                method: 'POST', path: `/api/datos-personales/${idPersonal}/oposicion`,
                body: JSON.stringify({ motivo: 'Solicitado por administrador', tipo_dato: 'biometria' })
            }
        };

        const cfg = endpoints[derecho];
        if (!cfg) return;

        if (derecho === 'supresion') {
            if (!window.confirm('⚠️ ADVERTENCIA: La supresión eliminará PERMANENTEMENTE todos los datos de esta persona incluyendo biometría, documentos y registros. ¿Estás seguro?')) return;
        }

        try {
            const res = await fetchWithAuth(`${API_BASE_URL}${cfg.path}`, {
                method: cfg.method,
                headers: cfg.body ? { 'Content-Type': 'application/json' } : {},
                body: cfg.body
            });

            if (res.ok) {
                const data = await res.json();
                if (derecho === 'portabilidad') {
                    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `datos-personales-${idPersonal}.json`;
                    a.click();
                    URL.revokeObjectURL(url);
                    alert('Datos exportados correctamente');
                } else if (derecho === 'supresion') {
                    alert('Datos suprimidos permanentemente');
                    setSearchResult(null);
                    setSearchRut('');
                } else {
                    alert(`Derecho de ${derecho} ejercido correctamente`);
                }
            } else {
                const data = await res.json();
                alert(data.error || `Error al ejercer derecho de ${derecho}`);
            }
        } catch (err) {
            alert('Error de conexión');
        }
    };

    const handleReportBreach = async () => {
        try {
            const res = await fetchWithAuth(`${API_BASE_URL}/api/data-breaches`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(breachForm)
            });
            if (res.ok) {
                alert('Violación reportada exitosamente');
                setShowBreachModal(false);
                setBreachForm({ titulo: '', descripcion: '', severidad: 'media', afectados_estimados: 0, tipos_dato_comprometidos: '' });
                fetchInitialData();
            } else {
                const data = await res.json();
                alert(data.error || 'Error al reportar');
            }
        } catch (err) {
            alert('Error de conexión');
        }
    };

    const handleUpdateRetention = async (id, dias) => {
        try {
            const res = await fetchWithAuth(`${API_BASE_URL}/api/retention-policies/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ dias_retencion: dias })
            });
            if (res.ok) {
                alert('Política actualizada');
                fetchInitialData();
            }
        } catch (err) {
            alert('Error de conexión');
        }
    };

    const handlePurgeNow = async () => {
        if (!window.confirm('¿Ejecutar purge de datos expirados ahora? Esta acción no se puede deshacer.')) return;
        try {
            const res = await fetchWithAuth(`${API_BASE_URL}/api/retention-policies/purge`, { method: 'POST' });
            if (res.ok) {
                const data = await res.json();
                alert(`Purge completado: ${data.eliminados || 0} registros eliminados`);
            }
        } catch (err) {
            alert('Error de conexión');
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '---';
        return new Date(dateStr).toLocaleString('es-CL');
    };

    const tabs = [
        { id: 'consentimientos', label: 'Consentimientos', icon: '✓' },
        { id: 'auditoria', label: 'Auditoría', icon: '📋' },
        { id: 'breaches', label: 'Violaciones', icon: '⚠️' },
        { id: 'retencion', label: 'Retención', icon: '🕒' },
        { id: 'dpa', label: 'DPA AWS', icon: '☁️' }
    ];

    if (loading) {
        return (
            <div className="manager-container">
                <p>Cargando panel de cumplimiento...</p>
            </div>
        );
    }

    return (
        <div className="manager-container">
            <div style={{ marginBottom: '20px' }}>
                <h2 style={{ fontSize: '1.5rem', marginBottom: '4px' }}>🛡️ Panel de Cumplimiento - Ley 21.719</h2>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                    Protección de Datos Personales - Chile
                </p>
            </div>

            {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}

            {/* Tabs */}
            <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '2px solid var(--border-light)', flexWrap: 'wrap' }}>
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        style={{
                            padding: '10px 16px',
                            border: 'none',
                            borderBottom: activeTab === tab.id ? '3px solid var(--primary)' : '3px solid transparent',
                            backgroundColor: activeTab === tab.id ? 'var(--bg-secondary, #f0f4f8)' : 'transparent',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: activeTab === tab.id ? '600' : '400',
                            color: activeTab === tab.id ? 'var(--primary)' : 'var(--text-muted)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                    >
                        <span>{tab.icon}</span> {tab.label}
                    </button>
                ))}
            </div>

            {/* TAB: Consentimientos + ARCO */}
            {activeTab === 'consentimientos' && (
                <div>
                    {/* Lista global de consentimientos */}
                    <div style={{ backgroundColor: 'var(--bg-secondary, #f8fafc)', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
                        <h3 style={{ fontSize: '1rem', marginBottom: '12px' }}>Consentimientos Registrados</h3>
                        {consentimientos && consentimientos.length > 0 ? (
                            <table className="standard-table" style={{ fontSize: '0.85rem' }}>
                                <thead>
                                    <tr>
                                        <th>Titular</th>
                                        <th>RUT</th>
                                        <th>Tipo Dato</th>
                                        <th>Estado</th>
                                        <th>Versión</th>
                                        <th>Fecha Consentimiento</th>
                                        <th>Fecha Revocación</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {consentimientos.map((c, i) => (
                                        <tr key={c.id || i}>
                                            <td>{c.nombre ? `${c.nombre} ${c.apellido || ''}` : '---'}</td>
                                            <td>{c.dni || '---'}</td>
                                            <td>{c.tipo_dato}</td>
                                            <td>
                                                <span className={`status-badge ${c.activo ? 'valid' : 'invalid'}`}>
                                                    {c.activo ? 'Activo' : 'Revocado'}
                                                </span>
                                            </td>
                                            <td>{c.version_politica || '---'}</td>
                                            <td>{formatDate(c.fecha_consentimiento)}</td>
                                            <td>{formatDate(c.fecha_revocacion)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>No hay consentimientos registrados.</p>
                        )}
                    </div>

                    <div style={{ backgroundColor: 'var(--bg-secondary, #f8fafc)', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
                        <h3 style={{ fontSize: '1rem', marginBottom: '12px' }}>Búsqueda de Titular - Derechos ARCO</h3>
                        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '12px' }}>
                            Busca una persona por RUT para ver su estado de consentimiento y ejercer derechos ARCO.
                        </p>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <input
                                type="text"
                                placeholder="RUT (ej: 12345678-9)"
                                value={searchRut}
                                onChange={(e) => setSearchRut(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearchPersonal()}
                                style={{ flex: 1, minWidth: '200px', padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border-light)' }}
                            />
                            <button onClick={handleSearchPersonal} className="login-button" style={{ padding: '8px 20px', margin: 0 }} disabled={searchLoading}>
                                {searchLoading ? 'Buscando...' : 'Buscar'}
                            </button>
                        </div>
                    </div>

                    {searchResult && (
                        <div style={{ backgroundColor: 'var(--bg-secondary, #f8fafc)', borderRadius: '12px', padding: '20px' }}>
                            <h3 style={{ fontSize: '1rem', marginBottom: '12px' }}>
                                {searchResult.persona.nombre} {searchResult.persona.apellido || ''}
                                <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginLeft: '8px' }}>
                                    RUT: {searchResult.persona.dni}
                                </span>
                            </h3>

                            {/* Consentimientos */}
                            <div style={{ marginBottom: '20px' }}>
                                <h4 style={{ fontSize: '0.9rem', marginBottom: '8px' }}>Estado de Consentimientos</h4>
                                {searchResult.consentimientos && searchResult.consentimientos.length > 0 ? (
                                    <table className="standard-table" style={{ fontSize: '0.85rem' }}>
                                        <thead>
                                            <tr>
                                                <th>Tipo Dato</th>
                                                <th>Estado</th>
                                                <th>Fecha Consentimiento</th>
                                                <th>Fecha Revocación</th>
                                                <th>Acción</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {searchResult.consentimientos.map((c, i) => (
                                                <tr key={i}>
                                                    <td>{c.tipo_dato}</td>
                                                    <td>
                                                        <span className={`status-badge ${c.activo ? 'valid' : 'invalid'}`}>
                                                            {c.activo ? 'Activo' : 'Revocado'}
                                                        </span>
                                                    </td>
                                                    <td>{formatDate(c.fecha_consentimiento)}</td>
                                                    <td>{formatDate(c.fecha_revocacion)}</td>
                                                    <td>
                                                        {c.activo && (
                                                            <button
                                                                onClick={() => handleRevokeConsent(searchResult.persona.id, c.tipo_dato)}
                                                                className="btn-action"
                                                                style={{ backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem' }}
                                                            >
                                                                Revocar
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Sin consentimientos registrados.</p>
                                )}
                            </div>

                            {/* Derechos ARCO */}
                            <div>
                                <h4 style={{ fontSize: '0.9rem', marginBottom: '8px' }}>Ejercer Derechos ARCO</h4>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                    <button onClick={() => handleARCO(searchResult.persona.id, 'acceso')} className="btn-action" style={{ backgroundColor: '#eff6ff', color: '#1e40af', border: '1px solid #dbeafe', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem' }}>
                                        📖 Acceso
                                    </button>
                                    <button onClick={() => handleARCO(searchResult.persona.id, 'portabilidad')} className="btn-action" style={{ backgroundColor: '#f0fdf4', color: '#166534', border: '1px solid #dcfce7', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem' }}>
                                        📤 Portabilidad
                                    </button>
                                    <button onClick={() => handleARCO(searchResult.persona.id, 'oposicion')} className="btn-action" style={{ backgroundColor: '#fffbeb', color: '#92400e', border: '1px solid #fef3c7', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem' }}>
                                        ✋ Oposición
                                    </button>
                                    <button onClick={() => handleARCO(searchResult.persona.id, 'supresion')} className="btn-action" style={{ backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem' }}>
                                        🗑️ Supresión
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* TAB: Auditoría */}
            {activeTab === 'auditoria' && (
                <div>
                    <h3 style={{ fontSize: '1rem', marginBottom: '12px' }}>Registro de Auditoría - Accesos a Datos Sensibles</h3>
                    {auditLog && auditLog.length > 0 ? (
                        <table className="standard-table" style={{ fontSize: '0.8rem' }}>
                            <thead>
                                <tr>
                                    <th>Fecha</th>
                                    <th>Acción</th>
                                    <th>Tipo Dato</th>
                                    <th>ID Registro</th>
                                    <th>Detalle</th>
                                    <th>IP</th>
                                </tr>
                            </thead>
                            <tbody>
                                {auditLog.slice(0, 50).map((log, i) => (
                                    <tr key={i}>
                                        <td>{formatDate(log.fecha)}</td>
                                        <td><span className="status-badge" style={{ fontSize: '0.7rem' }}>{log.accion}</span></td>
                                        <td>{log.tipo_dato || '---'}</td>
                                        <td style={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>{log.id_registro || '---'}</td>
                                        <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{log.detalle || '---'}</td>
                                        <td style={{ fontSize: '0.75rem' }}>{log.ip || '---'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <p style={{ color: 'var(--text-muted)' }}>No hay registros de auditoría.</p>
                    )}
                </div>
            )}

            {/* TAB: Violaciones */}
            {activeTab === 'breaches' && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h3 style={{ fontSize: '1rem' }}>Violaciones de Seguridad (Data Breaches)</h3>
                        <button onClick={() => setShowBreachModal(true)} className="login-button" style={{ padding: '8px 16px', margin: 0, fontSize: '0.8rem' }}>
                            + Reportar Violación
                        </button>
                    </div>
                    {breaches && breaches.length > 0 ? (
                        <table className="standard-table" style={{ fontSize: '0.8rem' }}>
                            <thead>
                                <tr>
                                    <th>Título</th>
                                    <th>Severidad</th>
                                    <th>Estado</th>
                                    <th>Afectados</th>
                                    <th>Detección</th>
                                    <th>Reporte Agencia</th>
                                    <th>Notif. Afectados</th>
                                </tr>
                            </thead>
                            <tbody>
                                {breaches.map((b, i) => (
                                    <tr key={i}>
                                        <td>{b.titulo}</td>
                                        <td>
                                            <span className={`status-badge ${b.severidad === 'critica' ? 'invalid' : b.severidad === 'alta' ? 'vencido' : 'warning'}`}>
                                                {b.severidad}
                                            </span>
                                        </td>
                                        <td>{b.estado}</td>
                                        <td>{b.afectados_estimados || 0}</td>
                                        <td>{formatDate(b.fecha_deteccion)}</td>
                                        <td>{formatDate(b.fecha_reporte_agencia)}</td>
                                        <td>{formatDate(b.fecha_notificacion_afectados)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <p style={{ color: 'var(--text-muted)' }}>No hay violaciones reportadas.</p>
                    )}

                    {showBreachModal && (
                        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowBreachModal(false)}>
                            <div style={{ backgroundColor: 'var(--bg-main, white)', borderRadius: '12px', padding: '24px', maxWidth: '500px', width: '90%' }} onClick={e => e.stopPropagation()}>
                                <h3 style={{ marginBottom: '16px' }}>Reportar Violación de Seguridad</h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    <input placeholder="Título" value={breachForm.titulo} onChange={e => setBreachForm({...breachForm, titulo: e.target.value})} style={{ padding: '8px', borderRadius: '8px', border: '1px solid var(--border-light)' }} />
                                    <textarea placeholder="Descripción" value={breachForm.descripcion} onChange={e => setBreachForm({...breachForm, descripcion: e.target.value})} style={{ padding: '8px', borderRadius: '8px', border: '1px solid var(--border-light)', minHeight: '80px' }} />
                                    <select value={breachForm.severidad} onChange={e => setBreachForm({...breachForm, severidad: e.target.value})} style={{ padding: '8px', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
                                        <option value="baja">Baja</option>
                                        <option value="media">Media</option>
                                        <option value="alta">Alta</option>
                                        <option value="critica">Crítica</option>
                                    </select>
                                    <input type="number" placeholder="Afectados estimados" value={breachForm.afectados_estimados} onChange={e => setBreachForm({...breachForm, afectados_estimados: parseInt(e.target.value) || 0})} style={{ padding: '8px', borderRadius: '8px', border: '1px solid var(--border-light)' }} />
                                    <input placeholder="Tipos de dato comprometidos" value={breachForm.tipos_dato_comprometidos} onChange={e => setBreachForm({...breachForm, tipos_dato_comprometidos: e.target.value})} style={{ padding: '8px', borderRadius: '8px', border: '1px solid var(--border-light)' }} />
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button onClick={() => setShowBreachModal(false)} className="btn-secondary" style={{ flex: 1 }}>Cancelar</button>
                                        <button onClick={handleReportBreach} className="login-button" style={{ flex: 1, margin: 0 }}>Reportar</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* TAB: Retención */}
            {activeTab === 'retencion' && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h3 style={{ fontSize: '1rem' }}>Políticas de Retención de Datos</h3>
                        <button onClick={handlePurgeNow} className="btn-action" style={{ backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem' }}>
                            🔄 Ejecutar Purge Ahora
                        </button>
                    </div>
                    {retentionPolicies && retentionPolicies.length > 0 ? (
                        <table className="standard-table" style={{ fontSize: '0.85rem' }}>
                            <thead>
                                <tr>
                                    <th>Tipo de Dato</th>
                                    <th>Días Retención</th>
                                    <th>Equivalente</th>
                                    <th>Descripción</th>
                                    <th>Activo</th>
                                    <th>Modificar</th>
                                </tr>
                            </thead>
                            <tbody>
                                {retentionPolicies.map((p, i) => (
                                    <tr key={i}>
                                        <td><strong>{p.tipo_dato}</strong></td>
                                        <td>{p.dias_retencion}</td>
                                        <td>{Math.round(p.dias_retencion / 365 * 10) / 10} años</td>
                                        <td style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{p.descripcion || '---'}</td>
                                        <td>
                                            <span className={`status-badge ${p.activo ? 'valid' : 'invalid'}`}>
                                                {p.activo ? 'Sí' : 'No'}
                                            </span>
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                defaultValue={p.dias_retencion}
                                                onBlur={(e) => e.target.value != p.dias_retencion && handleUpdateRetention(p.id, parseInt(e.target.value))}
                                                style={{ width: '80px', padding: '4px', borderRadius: '6px', border: '1px solid var(--border-light)', fontSize: '0.8rem' }}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <p style={{ color: 'var(--text-muted)' }}>No hay políticas configuradas.</p>
                    )}
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '12px' }}>
                        ℹ️ El purge automático se ejecuta cada 24 horas eliminando datos que exceden su período de retención.
                    </p>
                </div>
            )}

            {/* TAB: DPA AWS */}
            {activeTab === 'dpa' && dpaStatus && (
                <div>
                    <h3 style={{ fontSize: '1rem', marginBottom: '16px' }}>Acuerdo de Procesamiento de Datos (DPA) - AWS</h3>
                    <div style={{ backgroundColor: 'var(--bg-secondary, #f8fafc)', borderRadius: '12px', padding: '20px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                            <div>
                                <strong>Estado DPA:</strong>
                                <div style={{ marginTop: '4px' }}>
                                    <span className={`status-badge ${dpaStatus.dpa_firmado ? 'valid' : 'invalid'}`}>
                                        {dpaStatus.dpa_firmado ? '✓ Firmado' : '✗ Pendiente'}
                                    </span>
                                </div>
                            </div>
                            <div>
                                <strong>Región AWS:</strong>
                                <div style={{ marginTop: '4px' }}>{dpaStatus.region || '---'}</div>
                            </div>
                            <div>
                                <strong>Transferencia Internacional:</strong>
                                <div style={{ marginTop: '4px' }}>{dpaStatus.transferencia_internacional ? 'Sí' : 'No'}</div>
                            </div>
                            {dpaStatus.fecha_firma && (
                                <div>
                                    <strong>Fecha de Firma:</strong>
                                    <div style={{ marginTop: '4px' }}>{formatDate(dpaStatus.fecha_firma)}</div>
                                </div>
                            )}
                        </div>

                        {dpaStatus.recomendaciones && dpaStatus.recomendaciones.length > 0 && (
                            <div style={{ marginTop: '20px' }}>
                                <strong>Recomendaciones:</strong>
                                <ul style={{ marginTop: '8px', paddingLeft: '20px', fontSize: '0.85rem', color: 'var(--text-muted)', lineHeight: '1.8' }}>
                                    {dpaStatus.recomendaciones.map((r, i) => <li key={i}>{r}</li>)}
                                </ul>
                            </div>
                        )}

                        {!dpaStatus.dpa_firmado && (
                            <div style={{ marginTop: '20px', padding: '12px', backgroundColor: '#fffbeb', borderRadius: '8px', border: '1px solid #fef3c7' }}>
                                <p style={{ fontSize: '0.85rem', color: '#92400e' }}>
                                    ⚠️ El DPA con AWS no está firmado. Para cumplir con la Ley 21.719, debes firmar el
                                    Data Processing Agreement con AWS y configurar <code>AWS_DPA_SIGNED=true</code> en el archivo .env del backend.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
